import { Hash, Lock, LogOut, MessageSquareText, Plus, Search, Send, Trash2, X } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import bchannelLogo from "../../../../logo.png";
import { isBchannelAdmin } from "../../../shared/admin";
import { ApiError, api, type HomeData } from "../../api";
import { BannedState } from "../../components/AppStates";
import { Avatar, LoadingRaccoon } from "../../components/primitives";

type ChannelResult = { id: string; name: string; isPrivate: boolean };
type PingKind = "channel" | "here" | "none";

function SlackMark() {
  return (
    <svg className="slack-mark" viewBox="0 0 127.6 127.6" aria-hidden="true">
      <path
        fill="#e01e5a"
        d="M27.2 80c0 7.5-6.1 13.6-13.6 13.6S0 87.5 0 80s6.1-13.6 13.6-13.6h13.6V80zm6.8 0c0-7.5 6.1-13.6 13.6-13.6S61.2 72.5 61.2 80v34c0 7.5-6.1 13.6-13.6 13.6S34 121.5 34 114V80z"
      />
      <path
        fill="#36c5f0"
        d="M47.6 27.2C40.1 27.2 34 21.1 34 13.6S40.1 0 47.6 0s13.6 6.1 13.6 13.6v13.6H47.6zm0 6.8c7.5 0 13.6 6.1 13.6 13.6s-6.1 13.6-13.6 13.6h-34C6.1 61.2 0 55.1 0 47.6S6.1 34 13.6 34h34z"
      />
      <path
        fill="#2eb67d"
        d="M100.4 47.6c0-7.5 6.1-13.6 13.6-13.6s13.6 6.1 13.6 13.6-6.1 13.6-13.6 13.6h-13.6V47.6zm-6.8 0c0 7.5-6.1 13.6-13.6 13.6s-13.6-6.1-13.6-13.6v-34C66.4 6.1 72.5 0 80 0s13.6 6.1 13.6 13.6v34z"
      />
      <path
        fill="#ecb22e"
        d="M80 100.4c7.5 0 13.6 6.1 13.6 13.6s-6.1 13.6-13.6 13.6-13.6-6.1-13.6-13.6v-13.6H80zm0-6.8c-7.5 0-13.6-6.1-13.6-13.6S72.5 66.4 80 66.4h34c7.5 0 13.6 6.1 13.6 13.6S121.5 93.6 114 93.6H80z"
      />
    </svg>
  );
}

function SignedOutHome() {
  const authError = new URLSearchParams(location.search).get("auth_error");
  return (
    <main className="home-auth">
      <section className="home-auth-card">
        <img src={bchannelLogo} alt="" />
        <div>
          <h1>bChannel</h1>
          <p>Write better Slack messages with formatting, images, buttons, and channel pings.</p>
        </div>
        {authError && <p className="home-auth-error">{authError}</p>}
        <a className="slack-sign-in" href="/auth/slack">
          <SlackMark /> Sign in with Slack
        </a>
        <small>Sign in to see your messages and drafts.</small>
      </section>
    </main>
  );
}

function messageDate(value: string) {
  const date = new Date(value);
  const today = new Date();
  const yesterday = new Date(today);
  yesterday.setDate(today.getDate() - 1);
  const day = date.toDateString();
  if (day === today.toDateString()) return "Today";
  if (day === yesterday.toDateString()) return "Yesterday";
  return new Intl.DateTimeFormat(undefined, {
    month: "short",
    day: "numeric",
    ...(date.getFullYear() !== today.getFullYear() ? { year: "numeric" } : {}),
  }).format(date);
}

type HomeMessage = HomeData["drafts"][number];

function MessageGroup({
  title,
  messages,
  deleting,
  onDelete,
}: {
  title: string;
  messages: HomeMessage[];
  deleting?: string;
  onDelete?: (draft: HomeMessage) => void;
}) {
  if (!messages.length) return null;
  return (
    <section className="home-message-group">
      <h2>{title}</h2>
      <div className="home-drafts">
        {messages.map((draft) => (
          <div className="home-draft-row" key={draft.id}>
            <a href={`/compose/${draft.id}`} className="home-draft">
              <span className={`home-draft-icon ${draft.status}`}>
                {draft.status === "sent" ? <Send size={15} /> : <MessageSquareText size={15} />}
              </span>
              <span className="home-draft-copy">
                <strong>{draft.title}</strong>
                <small>
                  <Hash size={12} /> {draft.channelName}
                  {draft.ping !== "none" && <b>@{draft.ping}</b>}
                  {draft.status === "failed" && <em>Failed</em>}
                </small>
              </span>
              <time dateTime={draft.sentAt ?? draft.updatedAt}>{messageDate(draft.sentAt ?? draft.updatedAt)}</time>
            </a>
            {onDelete && draft.status !== "sending" && (
              <button
                type="button"
                className="home-draft-delete"
                aria-label={`Delete ${draft.title}`}
                title="Delete draft"
                disabled={deleting === draft.id}
                onClick={() => onDelete(draft)}
              >
                {deleting === draft.id ? <LoadingRaccoon size={16} /> : <Trash2 size={14} />}
              </button>
            )}
          </div>
        ))}
      </div>
    </section>
  );
}

function NewDraft({ onClose }: { onClose: () => void }) {
  const [query, setQuery] = useState("");
  const [channels, setChannels] = useState<ChannelResult[]>([]);
  const [channel, setChannel] = useState<ChannelResult | null>(null);
  const [ping, setPing] = useState<PingKind>("none");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (channel) return;
    const timer = window.setTimeout(() => {
      api
        .channels(query)
        .then(setChannels)
        .catch(() => setChannels([]));
    }, 180);
    return () => window.clearTimeout(timer);
  }, [channel, query]);

  const create = async () => {
    if (!channel || busy) return;
    setBusy(true);
    setError("");
    try {
      const result = await api.createHomeDraft({
        channelId: channel.id,
        channelName: channel.name,
        isPrivate: channel.isPrivate,
        ping,
      });
      location.assign(`/compose/${result.draftId}`);
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : "Could not create this draft.");
      setBusy(false);
    }
  };

  return (
    <div className="new-draft-layer">
      <button type="button" className="new-draft-backdrop" aria-label="Close new message" onClick={onClose} />
      <section className="new-draft" role="dialog" aria-modal="true" aria-labelledby="new-draft-title">
        <header>
          <div>
            <span>New message</span>
            <strong id="new-draft-title">Where should it go?</strong>
          </div>
          <button type="button" aria-label="Close" onClick={onClose}>
            <X size={17} />
          </button>
        </header>

        <div className="new-draft-channel">
          <label htmlFor="home-channel-search">Channel</label>
          {channel ? (
            <button type="button" className="selected-channel" onClick={() => setChannel(null)}>
              {channel.isPrivate ? <Lock size={14} /> : <Hash size={14} />}
              <strong>{channel.name}</strong>
              <span>Change</span>
            </button>
          ) : (
            <>
              <div className="home-channel-search">
                <Search size={15} />
                <input
                  id="home-channel-search"
                  value={query}
                  onChange={(event) => setQuery(event.target.value)}
                  placeholder="Search channels"
                  autoComplete="off"
                />
              </div>
              <div className="home-channel-results">
                {channels.map((item) => (
                  <button type="button" key={item.id} onClick={() => setChannel(item)}>
                    {item.isPrivate ? <Lock size={14} /> : <Hash size={14} />}
                    <span>{item.name}</span>
                  </button>
                ))}
                {!channels.length && <small>{query ? "No matching channels" : "Start typing to find a channel"}</small>}
              </div>
            </>
          )}
        </div>

        <fieldset className="new-draft-kind">
          <legend>Message type</legend>
          {(
            [
              ["none", "Message", "No channel-wide notification"],
              ["here", "@here", "Notify active people"],
              ["channel", "@channel", "Notify everyone in the channel"],
            ] as const
          ).map(([value, label, description]) => (
            <label key={value}>
              <input type="radio" name="ping" checked={ping === value} onChange={() => setPing(value)} />
              <span>
                <strong>{label}</strong>
                <small>{description}</small>
              </span>
            </label>
          ))}
        </fieldset>
        {ping !== "none" && (
          <p className="new-draft-note">Sending will still require Channel Manager or creator permission.</p>
        )}
        {error && <p className="new-draft-error">{error}</p>}
        <footer>
          <button type="button" onClick={onClose}>
            Cancel
          </button>
          <button type="button" className="new-draft-create" disabled={!channel || busy} onClick={create}>
            {busy ? <LoadingRaccoon size={18} /> : <Plus size={16} />}
            Create draft
          </button>
        </footer>
      </section>
    </div>
  );
}

function SignedInHome({ data }: { data: HomeData }) {
  const [query, setQuery] = useState("");
  const [creating, setCreating] = useState(false);
  const [items, setItems] = useState(data.drafts);
  const [deleting, setDeleting] = useState<string>();
  const messages = useMemo(() => {
    const normalized = query.trim().toLowerCase();
    const filtered = normalized
      ? items.filter((draft) =>
          `${draft.title} ${draft.channelName} ${draft.ping} ${draft.status}`.toLowerCase().includes(normalized),
        )
      : items;
    const newestFirst = (left: HomeMessage, right: HomeMessage) =>
      new Date(right.sentAt ?? right.updatedAt).getTime() - new Date(left.sentAt ?? left.updatedAt).getTime();
    return {
      drafts: filtered.filter((draft) => draft.status !== "sent").sort(newestFirst),
      sent: filtered.filter((draft) => draft.status === "sent").sort(newestFirst),
    };
  }, [items, query]);
  const empty = messages.drafts.length === 0 && messages.sent.length === 0;
  const deleteDraft = async (draft: HomeMessage) => {
    if (deleting) return;
    setDeleting(draft.id);
    try {
      await api.deleteHomeDraft(draft.id);
      setItems((current) => current.filter((item) => item.id !== draft.id));
    } catch (cause) {
      window.alert(cause instanceof Error ? cause.message : "Could not delete this draft.");
    } finally {
      setDeleting(undefined);
    }
  };
  return (
    <main className="home-page">
      <header className="home-header">
        <a href="/" className="home-brand">
          <img src={bchannelLogo} alt="" />
          <strong>bChannel</strong>
        </a>
        <div className="home-person">
          {isBchannelAdmin(data.user.id) && <a href="/admin">Admin</a>}
          <span>{data.user.name}</span>
          <Avatar name={data.user.name} src={data.user.avatarUrl} size={30} />
          <button
            type="button"
            aria-label="Sign out"
            title="Sign out"
            onClick={() => api.logout().finally(() => location.reload())}
          >
            <LogOut size={15} />
          </button>
        </div>
      </header>
      <section className="home-main">
        <div className="home-title-row">
          <h1>Messages</h1>
          <button type="button" className="home-new" onClick={() => setCreating(true)}>
            <Plus size={16} /> New
          </button>
        </div>
        <label className="home-draft-search">
          <Search size={15} />
          <input value={query} onChange={(event) => setQuery(event.target.value)} placeholder="Search" />
        </label>
        <MessageGroup title="Drafts" messages={messages.drafts} deleting={deleting} onDelete={deleteDraft} />
        <MessageGroup title="Sent" messages={messages.sent} />
        {empty && <div className="home-empty">{query ? "No matches" : "No messages yet"}</div>}
      </section>
      {creating && <NewDraft onClose={() => setCreating(false)} />}
    </main>
  );
}

export function HomePage() {
  const [data, setData] = useState<HomeData | null>(null);
  const [signedOut, setSignedOut] = useState(false);
  const [error, setError] = useState("");
  const [banned, setBanned] = useState(false);
  useEffect(() => {
    api
      .home()
      .then(setData)
      .catch((cause) => {
        if (cause instanceof ApiError && cause.status === 401) setSignedOut(true);
        else if (cause instanceof ApiError && cause.status === 403 && cause.message.includes("banned")) setBanned(true);
        else setError(cause instanceof Error ? cause.message : "Could not load bChannel.");
      });
  }, []);
  if (banned) return <BannedState />;
  if (signedOut) return <SignedOutHome />;
  if (error) return <SignedOutHome />;
  if (!data)
    return (
      <main className="loading-screen">
        <img className="loading-mark" src={bchannelLogo} alt="bChannel" />
        <LoadingRaccoon size={42} />
        <span>Opening bChannel…</span>
      </main>
    );
  return <SignedInHome data={data} />;
}
